import { Fragment, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { tripService, vehicleService, driverService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SkeletonTable } from "@/components/common/Skeletons";
import { NoResults } from "@/components/common/EmptyState";
import { LoadMoreSentinel } from "@/components/common/LoadMoreSentinel";
import { clockTime, dateSectionLabel, zonedDateKey, fmtKm, fmtDuration, fmtL } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronRight, Circle, Loader2 } from "lucide-react";

const TRIP_COLUMN_COUNT = 12;

// Buckets trips into same-calendar-day groups — newest date first, and
// newest time first within each date — a date header per group replaces
// the old per-row date subtext under the start time. Grouped by each
// trip's own Bouncie-reported local day (zonedDateKey), not the viewing
// browser's day, so a late-night trip lands under the same date Bouncie's
// own app shows it under.
//
// Sorts explicitly rather than trusting the API's own order: list_all()
// (what this page hits with no vehicle/status filter picked) has no
// order_by on the backend at all, so relying on "the API already returns
// newest-first" was silently wrong whenever that path was taken.
function groupTripsByDate(trips) {
  const sorted = [...trips].sort((a, b) => {
    const at = a.startTime ? new Date(a.startTime).getTime() : -Infinity;
    const bt = b.startTime ? new Date(b.startTime).getTime() : -Infinity;
    return bt - at; // newest first
  });

  const groups = [];
  const byKey = new Map();
  for (const t of sorted) {
    const key = zonedDateKey(t.startTime, t.utcOffsetMinutes);
    let group = byKey.get(key);
    if (!group) {
      group = { key, date: t.startTime, offsetMinutes: t.utcOffsetMinutes, trips: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.trips.push(t); // sorted newest-first above, so this stays newest-first too
  }
  return groups; // groups themselves are encountered newest-date-first, same reason
}

export default function Trips() {
  const navigate = useNavigate();
  const [vehicleId, setVehicleId] = useState("all");
  const [driverId, setDriverId] = useState("all");
  const [status, setStatus] = useState("all");

  // Page 1 is the live table (nightly-archived down to the last ~20 days —
  // see backend/app/services/archival_service.py), which is small enough to
  // fetch in one shot. Every page after that walks further back through
  // trip_archive in 20-day windows, driven by the archive endpoint's own
  // `window_start`/`has_more` rather than anything computed client-side, so
  // an empty window (or one entirely filtered out below) still advances.
  const fetchPage = useCallback(async (cursor) => {
    if (cursor === undefined) {
      const trips = await tripService.list({ vehicleId, driverId, status });
      const sorted = [...trips].sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      return { items: sorted, nextCursor: new Date().toISOString(), hasMore: true };
    }
    const { trips, windowStart, hasMore } = await tripService.archive({ vehicleId, before: cursor, windowDays: 20 });
    let filtered = trips;
    if (driverId !== "all") filtered = filtered.filter((t) => t.driverId === driverId);
    if (status !== "all") filtered = filtered.filter((t) => t.status === status);
    return { items: filtered, nextCursor: windowStart, hasMore };
  }, [vehicleId, driverId, status]);

  const { items: trips, loading, loadingMore, hasMore, loadMore } = useInfiniteScroll(fetchPage, [vehicleId, driverId, status]);

  // Filter option lists — fetched separately from the table data itself so
  // they always show every vehicle/driver, not just ones with trips.
  const { data: vehicleOptions } = useFetch(() => vehicleService.list(), []);
  const { data: driverOptions } = useFetch(() => driverService.list(), []);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Trips" subtitle="Every journey across your fleet, with route and safety detail." />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={vehicleId} onValueChange={setVehicleId}>
          <SelectTrigger className="h-10 w-full sm:w-48" data-testid="trips-vehicle-filter"><SelectValue placeholder="Vehicle" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All vehicles</SelectItem>{(vehicleOptions || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="h-10 w-full sm:w-48" data-testid="trips-driver-filter"><SelectValue placeholder="Driver" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All drivers</SelectItem>{(driverOptions || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-full sm:w-40" data-testid="trips-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="in-progress">In progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
        </Select>
      </div>

      {loading ? <SkeletonTable rows={7} cols={8} /> : trips.length === 0 ? <NoResults title="No trips for this filter" description="No trips match the selected vehicle, driver or status." /> : (
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border/60">
                  <TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead>
                  <TableHead>Duration</TableHead><TableHead>Distance</TableHead><TableHead>Fuel</TableHead>
                  <TableHead>Max</TableHead><TableHead>Avg</TableHead><TableHead>Braking</TableHead><TableHead>Accel</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupTripsByDate(trips).map((group) => (
                  <Fragment key={group.key}>
                    <TableRow className="hover:bg-transparent border-border/50 bg-secondary/20">
                      <TableCell colSpan={TRIP_COLUMN_COUNT} className="py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {dateSectionLabel(group.date, group.offsetMinutes)}
                      </TableCell>
                    </TableRow>
                    {group.trips.map((t) => {
                      const v = vehicleService.byId(t.vehicleId);
                      const d = t.driverId ? driverService.byId(t.driverId) : null;
                      return (
                        <TableRow key={t.id} onClick={() => navigate(`/trips/${t.id}`)} className="cursor-pointer border-border/50" data-testid={`trip-row-${t.id}`}>
                          <TableCell className="font-medium">{v?.name}</TableCell>
                          <TableCell className="text-sm">{d?.name || <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="text-sm">{clockTime(t.startTime, t.utcOffsetMinutes)}</TableCell>
                          <TableCell className="text-sm">{t.endTime ? clockTime(t.endTime, t.utcOffsetMinutes) : <span className="inline-flex items-center gap-1 text-emerald-400"><Circle className="h-2 w-2 fill-emerald-400" /> Live</span>}</TableCell>
                          <TableCell className="font-telemetry text-sm">{fmtDuration(t.durationMin)}</TableCell>
                          <TableCell className="font-telemetry text-sm">{fmtKm(t.distanceKm)}</TableCell>
                          <TableCell className="font-telemetry text-sm">{fmtL(t.fuelL)}</TableCell>
                          <TableCell className="font-telemetry text-sm">{t.maxSpeed.toFixed(1)}</TableCell>
                          <TableCell className="font-telemetry text-sm">{t.avgSpeed}</TableCell>
                          <TableCell><span className={cn("font-telemetry text-sm", t.hardBraking > 0 ? "text-amber-400" : "text-muted-foreground")}>{t.hardBraking}</span></TableCell>
                          <TableCell><span className={cn("font-telemetry text-sm", t.hardAccel > 0 ? "text-amber-400" : "text-muted-foreground")}>{t.hardAccel}</span></TableCell>
                          <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          {hasMore && (
            <>
              <LoadMoreSentinel onVisible={loadMore} disabled={loadingMore} testId="trips-load-more" />
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 border-t border-border/50 py-4 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading older trips…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
